import React from "react";
import { ModelGeoCampaign } from "@models/ModelGeoCampaign";

interface InfoWindowProps {
  marker: ModelGeoCampaign;
  S3_URL: string;
  registrationText: string;
  themeClass: {
    bg: (key: string) => string;
  };
  variant?: "regular" | "detail";
  onClose?: () => void;
  onDetailClick?: (id: number) => void;
}

// Helper function to remove HTML tags (keeping line breaks)
const stripHtmlTags = (html: string): string => {
  return html
    .replace(/<br\s*\/?>/gi, "\n") // Convert br tags to line breaks
    .replace(/<\/p>/gi, "\n") // Convert closing p tags to line breaks
    .replace(/<p[^>]*>/gi, "") // Remove opening p tags
    .replace(/<[^>]*>/g, "") // Remove other HTML tags
    .replace(/\n\s*\n/g, "\n") // Remove multiple consecutive line breaks
    .trim();
};

// React Component (for potential future use)
export const InfoWindow: React.FC<InfoWindowProps> = ({
  marker,
  S3_URL,
  registrationText,
  themeClass,
  variant = "regular",
  onClose,
  onDetailClick,
}) => {
  const cleanReward = stripHtmlTags(marker.reward ?? "");

  const handleDetailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onDetailClick) {
      onDetailClick(marker.id ?? 0);
    }
  };

  if (variant === "detail") {
    return (
      <div className="relative bg-white rounded-2xl shadow-2xl w-80 overflow-hidden">
        <div className="relative h-56">
          <img
            src={`${S3_URL}${marker.thumbnail}`}
            alt="Campaign Image"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

          <button
            id="close-info-window"
            className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 hover:text-gray-900 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition-all hover:shadow-lg"
            onClick={onClose}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13M1 1L13 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-green-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="7"
                    cy="7"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M7 3V7L9.5 9.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                {registrationText}
              </span>
            </div>
            <h3 className="text-lg font-bold text-white drop-shadow-lg line-clamp-1">
              {marker.title}
            </h3>
          </div>
        </div>

        <div className="p-4">
          <p
            className="text-sm text-gray-600 mb-4 line-clamp-2"
            style={{ lineHeight: "1.6" }}
          >
            {cleanReward}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-4">
                {(marker.total_apply || 0) >= 1 && (
                  <div className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                {(marker.total_apply || 0) >= 2 && (
                  <div className="w-8 h-8 rounded-full bg-purple-500 border-2 border-white flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                {(marker.total_apply || 0) >= 3 && (
                  <div className="w-8 h-8 rounded-full bg-pink-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    +{(marker.total_apply || 0) - 2}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500">신청인원</p>
                <p className="text-sm font-bold">
                  {marker.total_apply}
                  <span className="text-gray-400 font-normal">
                    /{marker.max_apply}
                  </span>
                </p>
              </div>
            </div>

            <a
              href={`/detailPage/${marker.id}`}
              onClick={handleDetailClick}
              className={`inline-flex items-center gap-2 ${themeClass.bg(
                "text"
              )} text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:shadow-lg transition-all`}
            >
              상세보기
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Regular variant - Minimal style
  return (
    <div className="relative bg-white rounded-xl shadow-xl w-64 overflow-hidden">
      <button
        id="close-info-window"
        className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm text-gray-600 hover:text-gray-800 rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10"
        onClick={onClose}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 1L1 11M1 1L11 11"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className="p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
            <img
              src={`${S3_URL}${marker.thumbnail}`}
              alt="Campaign Image"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1">
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="4"
                  cy="4"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <path
                  d="M4 2V4L5.5 5.5"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
              {registrationText}
            </span>

            <h3 className="text-sm font-bold text-gray-900 line-clamp-1 mb-1">
              {marker.title}
            </h3>

            <p
              className="text-xs text-gray-600 line-clamp-2 mb-2"
              style={{ lineHeight: "1.4" }}
            >
              {cleanReward}
            </p>

            <div className="flex items-center gap-1 text-xs">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 7C8.65685 7 10 5.65685 10 4C10 2.34315 8.65685 1 7 1C5.34315 1 4 2.34315 4 4C4 5.65685 5.34315 7 7 7Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M7 8.5C3.41015 8.5 0.5 10.9101 0.5 13.5H13.5C13.5 10.9101 10.5899 8.5 7 8.5Z"
                  fill="#E5E7EB"
                />
              </svg>
              <span className="font-bold text-gray-900">
                {marker.total_apply}
              </span>
              <span className="text-gray-500">/{marker.max_apply}</span>
            </div>
          </div>
        </div>

        <a
          href={`/detailPage/${marker.id}`}
          onClick={handleDetailClick}
          className={`block w-full text-center ${themeClass.bg(
            "text"
          )} text-white text-xs font-bold py-2 rounded-lg mt-3 hover:opacity-90 transition-opacity`}
        >
          상세보기
        </a>
      </div>

      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-white"></div>
    </div>
  );
};

// Function to generate HTML string for Naver Maps InfoWindow
export const getInfoWindowHTML = ({
  marker,
  S3_URL,
  registrationText,
  themeClass,
  variant = "regular",
}: Omit<InfoWindowProps, "onClose" | "onDetailClick">): string => {
  const cleanReward = stripHtmlTags(marker.reward ?? "");

  if (variant === "detail") {
    return `
      <div class="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden relative" style="margin: 0 0 -5px 0;">
        <div class="relative h-56">
          <img
            src="${S3_URL}${marker.thumbnail}"
            alt="Campaign Image"
            class="w-full h-full object-cover"
          />
          <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          
          <button class="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 hover:text-gray-900 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition-all hover:shadow-lg" id="close-info-window">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>

          <div class="absolute bottom-4 left-4 right-4">
            <div class="flex items-center gap-2 mb-2">
              <span class="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-green-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M7 3V7L9.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                ${registrationText}
              </span>
            </div>
            <h3 class="text-lg font-bold text-white drop-shadow-lg line-clamp-1">
              ${marker.title}
            </h3>
          </div>
        </div>

        <div class="p-4">
          <p class="text-sm text-gray-600 mb-4 line-clamp-2" style="line-height: 1.6;">
            ${cleanReward}
          </p>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="flex -space-x-4">
                ${(marker.total_apply || 0) >= 1 ? `
                  <div class="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                ` : ''}
                ${(marker.total_apply || 0) >= 2 ? `
                  <div class="w-8 h-8 rounded-full bg-purple-500 border-2 border-white flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                ` : ''}
                ${(marker.total_apply || 0) >= 3 ? `
                  <div class="w-8 h-8 rounded-full bg-pink-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    +${(marker.total_apply || 0) - 2}
                  </div>
                ` : ''}
              </div>
              <div>
                <p class="text-xs text-gray-500">참여자</p>
                <p class="text-sm font-bold">
                  ${marker.total_apply}
                  <span class="text-gray-400 font-normal">/${
                    marker.max_apply
                  }</span>
                </p>
              </div>
            </div>

            <a
              href="/detailPage/${marker.id}"
              class="inline-flex items-center gap-2 ${themeClass.bg(
                "text"
              )} text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:shadow-lg transition-all"
            >
              상세보기
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  // Regular variant - Minimal style
  return `
    <div class="bg-white rounded-xl shadow-xl w-64 overflow-hidden relative" style="margin: 0 0 -5px 0;">
      <button class="absolute top-2 right-2 bg-white/80 backdrop-blur-sm text-gray-600 hover:text-gray-800 rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10" id="close-info-window">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 1L1 11M1 1L11 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>

      <div class="p-4">
        <div class="flex gap-3">
          <div class="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
            <img
              src="${S3_URL}${marker.thumbnail}"
              alt="Campaign Image"
              class="w-full h-full object-cover"
            />
          </div>

          <div class="flex-1 min-w-0">
            <span class="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1">
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="4" cy="4" r="3" stroke="currentColor" stroke-width="1"/>
                <path d="M4 2V4L5.5 5.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
              </svg>
              ${registrationText}
            </span>

            <h3 class="text-sm font-bold text-gray-900 line-clamp-1 mb-1">
              ${marker.title}
            </h3>

            <p class="text-xs text-gray-600 line-clamp-2 mb-2" style="line-height: 1.4;">
              ${cleanReward}
            </p>

            <div class="flex items-center gap-1 text-xs">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 7C8.65685 7 10 5.65685 10 4C10 2.34315 8.65685 1 7 1C5.34315 1 4 2.34315 4 4C4 5.65685 5.34315 7 7 7Z" fill="#E5E7EB"/>
                <path d="M7 8.5C3.41015 8.5 0.5 10.9101 0.5 13.5H13.5C13.5 10.9101 10.5899 8.5 7 8.5Z" fill="#E5E7EB"/>
              </svg>
              <span class="font-bold text-gray-900">${marker.total_apply}</span>
              <span class="text-gray-500">/${marker.max_apply}</span>
            </div>
          </div>
        </div>

        <a
          href="/detailPage/${marker.id}"
          class="block w-full text-center ${themeClass.bg(
            "text"
          )} text-white text-xs font-bold py-2 rounded-lg mt-3 hover:opacity-90 transition-opacity"
        >
          상세보기
        </a>
      </div>

      <div class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-white"></div>
    </div>
  `;
};
